import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, User } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { getUserHabits } from '@/lib/firebase/queries/habits';
import type { User as UserType } from '@/types';
import type { Habit } from '@/types/habits';

interface UserHabitsProps {
  user: UserType;
  onUpdate?: () => void;
}

export function UserHabits({ user, onUpdate }: UserHabitsProps) {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  useEffect(() => {
    loadUserHabits();
  }, [user.id]);

  const loadUserHabits = async () => {
    try {
      setLoading(true);
      
      // Use the getUserHabits function which calculates values from habitLogs
      const userHabits = await getUserHabits(user.id);
      setHabits(userHabits);
    } catch (error) {
      console.error('Error loading user habits:', error);
      toast.error('Failed to load user habits');
    } finally {
      setLoading(false);
    }
  };

  const getHabitDisplayName = (habit: Habit) => {
    if (habit.isPrivate) {
      return t('habits.privateHabit');
    } else {
      return habit.name;
    }
  };

  const getProgressPercentage = (habit: Habit) => {
    return habit.progress || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" dir={dir}>
      <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} justify-between items-center`}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h3 className="text-lg font-semibold">
            {t('dashboard.admin.users.habits.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            View the user's personal habits and progress
          </p>
        </div>
      </div>

      {habits.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-6">
            <User className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-center">
              No habits found for this user
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {habits.map((habit) => (
            <Card key={habit.id} className="p-4">
              <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} justify-between items-center gap-4`}>
                <div className="flex-1">
                  <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} items-center gap-3`}>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h4 className="font-semibold text-base">
                        {getHabitDisplayName(habit)}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {habit.category}
                      </p>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      <User className="h-4 w-4" />
                      <span className="ml-1">Personal</span>
                    </Badge>
                  </div>
                  
                  <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} gap-4 mt-2 text-xs text-muted-foreground`}>
                    <span>
                      Progress: {getProgressPercentage(habit)}%
                    </span>
                    <span>
                      Streak: {habit.streak}
                    </span>
                    <span>
                      Days Completed: {habit.daysCompleted}
                    </span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-[#7dda55] h-2 rounded-full transition-all duration-300"
                      style={{ width: `${getProgressPercentage(habit)}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}